import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, FormControlLabel, Switch, TablePagination, Typography,
} from '@mui/material';
import {
  CardWrapper, SectionTitle, CampoIdentificacao,
} from '../../UI';
import InfoAdicionaisAcordo, { somarTotalAcordos } from '../Recebimentos/InfoAdicionaisAcordo';
import TableAcordos from '../Recebimentos/TableAcordos';
import * as C from '../../../theme/colors.constants';

const OPCOES_TAMANHO_PAGINA_ACORDOS = [10, 15, 20];

/**
 * Aba Recebimentos.
 *
 * Print `grid_check_recebimentos.png`: cabeçalho com Cliente/Devedor/Responsável,
 * o switch "Exibir apenas Acordos Ativos e Propostas", a tabela de acordos com
 * 7 ícones de ação por linha, e os 5 cards-contadores no rodapé.
 *
 * Cabeçalho (AW2-1133 Etapa 2) reaproveita `CampoIdentificacao` — mesmo
 * padrão visual de `CabecalhoFichaFinanceira.jsx` (Código/Nome de Cliente e
 * Devedor + Responsável). Nome vem de `processo` (síncrono, já disponível na
 * seleção da grid); código vem de `dadosProcesso` (assíncrono, já buscado
 * pelo container via `buscarAcompanhamentoDoProcessoService`) — evita
 * flicker "—" ao trocar de processo, mesma decisão já validada em Financeira.
 *
 * Grid "Acordos" (AW2-1133 Etapa 3) é dado real, paginado/ordenável, via
 * `TableAcordos.jsx` — mesmo padrão de visibilidade já usado em
 * Títulos/Antecedentes (esconde atrás de "Carregando.../Não encontrado"
 * quando vazia; `TablePagination` visível por `acordosTotal > 0`, não por
 * `.length`). Ações da grid (7 ícones + chevron) são só placeholder visual
 * nesta etapa — comportamento real vem em etapas futuras do plano.
 */
export default function AbaRecebimento({
  processo, dadosProcesso, infoAdicionaisAcordo,
  acordos, acordosTotal, acordosPagina, acordosTamanhoPagina, acordosOrdenacao,
  onChangeOrdenacaoAcordos, onChangePaginaAcordos, onChangeTamanhoPaginaAcordos,
  exibirApenasAtivos, onAlternarExibirApenasAtivos,
  podeModificarFase, idFaseProcesso, ehFaseAcordoSeguro, onAcordoMutado,
}) {
  return (
    <CardWrapper>
      <SectionTitle>Recebimentos</SectionTitle>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
          rowGap: 1.5,
          columnGap: 2,
          backgroundColor: C.paleGrey,
          border: `1px solid ${C.borderGrey}`,
          borderRadius: 1.5,
          p: 2,
          mb: 2,
        }}
      >
        <CampoIdentificacao label="Código do Cliente" value={dadosProcesso?.codigoCliente} />
        <CampoIdentificacao label="Nome do Cliente" value={processo?.nomeCliente} />
        <CampoIdentificacao label="Código do Devedor" value={dadosProcesso?.codigoDevedor} />
        <CampoIdentificacao label="Nome do Devedor" value={processo?.nomeDevedor} />
        <CampoIdentificacao label="Responsável" value={processo?.negociador} />
      </Box>

      <FormControlLabel
        control={<Switch checked={exibirApenasAtivos} onChange={onAlternarExibirApenasAtivos} />}
        label={<Typography variant="body2">Exibir apenas Acordos Ativos e Propostas</Typography>}
        sx={{ mb: 1 }}
      />

      {acordos?.length ? (
        <Box sx={{ overflowX: 'auto' }}>
          <TableAcordos
            acordos={acordos}
            ordenacao={acordosOrdenacao}
            onChangeOrdenacao={onChangeOrdenacaoAcordos}
            idProcesso={processo?.id}
            idFaseProcesso={idFaseProcesso}
            podeModificarFase={podeModificarFase}
            ehFaseAcordoSeguro={ehFaseAcordoSeguro}
            onAcordoMutado={onAcordoMutado}
          />
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: C.warmGrey, textAlign: 'center', py: 3 }}>
          {acordos == null ? 'Carregando...' : 'Não foram encontrados Acordos para este Processo.'}
        </Typography>
      )}
      {acordosTotal > 0 && (
        <TablePagination
          component="div"
          count={acordosTotal}
          page={acordosPagina}
          rowsPerPage={acordosTamanhoPagina}
          rowsPerPageOptions={OPCOES_TAMANHO_PAGINA_ACORDOS}
          onPageChange={(event, novaPagina) => onChangePaginaAcordos(novaPagina)}
          onRowsPerPageChange={onChangeTamanhoPaginaAcordos}
          labelRowsPerPage="Linhas por página:"
        />
      )}

      <Box sx={{ mt: 2.5 }}>
        <InfoAdicionaisAcordo dados={infoAdicionaisAcordo} />
      </Box>

      {/* Soma dos 5 cards, nunca `acordosTotal` (paginação da grid, afetada
          pelo toggle) — ver `somarTotalAcordos`, achado em QA ao vivo
          (Etapa 12): o 5º critério de aceite exige que este total bata
          sempre com a soma dos cards, independente do estado do toggle. */}
      <Typography sx={{ fontWeight: 700, mt: 2 }}>{`Total de acordos: ${somarTotalAcordos(infoAdicionaisAcordo)}`}</Typography>
    </CardWrapper>
  );
}
AbaRecebimento.propTypes = {
  processo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    negociador: PropTypes.string,
    nomeCliente: PropTypes.string,
    nomeDevedor: PropTypes.string,
  }),
  dadosProcesso: PropTypes.shape({
    codigoCliente: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    codigoDevedor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  infoAdicionaisAcordo: PropTypes.shape({
    qtdAcordosQuitados: PropTypes.number,
    qtdAcordosAtivos: PropTypes.number,
    qtdAcordosQuebrados: PropTypes.number,
    qtdAcordosSimulados: PropTypes.number,
    qtdAcordosSimulacoesPendentes: PropTypes.number,
  }),
  acordos: PropTypes.array,
  acordosTotal: PropTypes.number,
  acordosPagina: PropTypes.number,
  acordosTamanhoPagina: PropTypes.number,
  acordosOrdenacao: PropTypes.shape({
    campo: PropTypes.string,
    direcao: PropTypes.oneOf(['ASC', 'DESC']),
  }),
  onChangeOrdenacaoAcordos: PropTypes.func,
  onChangePaginaAcordos: PropTypes.func,
  onChangeTamanhoPaginaAcordos: PropTypes.func,
  exibirApenasAtivos: PropTypes.bool,
  onAlternarExibirApenasAtivos: PropTypes.func,
  podeModificarFase: PropTypes.bool,
  idFaseProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ehFaseAcordoSeguro: PropTypes.bool,
  onAcordoMutado: PropTypes.func,
};
AbaRecebimento.defaultProps = {
  processo: null,
  dadosProcesso: null,
  infoAdicionaisAcordo: null,
  acordos: null,
  acordosTotal: 0,
  acordosPagina: 0,
  acordosTamanhoPagina: 10,
  acordosOrdenacao: { campo: 'nroAcordo', direcao: 'DESC' },
  onChangeOrdenacaoAcordos: null,
  onChangePaginaAcordos: null,
  onChangeTamanhoPaginaAcordos: null,
  exibirApenasAtivos: true,
  onAlternarExibirApenasAtivos: null,
  podeModificarFase: false,
  idFaseProcesso: null,
  ehFaseAcordoSeguro: false,
  onAcordoMutado: null,
};
